import { useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { nextFileNo } from "@/lib/clinic";
import { uuid } from "@/lib/format";
import { BLOOD, MARITAL, WILAYAS } from "@/lib/clinic";
import { BigButton, Chip, Field, inputCls } from "@/components/ui-kit";

const EMPTY = {
  name: "", father_name: "", mother_name: "", file_no: "", phone: "", phone2: "", email: "", birth_date: "", birth_place: "", gender: "", nationality: "", id_number: "", marital: "", occupation: "", education: "",
  wilaya: "", daira: "", address: "", guardian_name: "", guardian_relation: "", guardian_phone: "", emergency_contact: "", emergency_phone: "", emergency_relation: "",
  insurer: "", policy_no: "", referred_by: "", blood_type: "", height: "", weight: "", allergies: "", chronic: "", medications: "", surgeries: "", family_history: "", vaccination: "", smoking: "", disability: "", notes: "",
};

const Section = ({ title, children, cols = "sm:grid-cols-2 lg:grid-cols-3" }) => (
  <div className="rounded border border-[#CCCCCC] bg-white p-3">
    <h3 className="font-bold text-sm text-[#164660] mb-2 border-b border-[#E5E5E5] pb-1">{title}</h3>
    <div className={`grid ${cols} gap-3`}>{children}</div>
  </div>
);

export default function PatientForm({ patient, onSaved }) {
  const { t, dispatch, view } = useApp();
  const [f, setF] = useState({ ...EMPTY, ...(patient || {}) });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const inp = (k, { type = "text", ltr, label } = {}) => (
    <Field label={label || t(`pf_${k}`)}><input type={type} dir={ltr ? "ltr" : undefined} value={f[k] ?? ""} onChange={set(k)} className={inputCls} data-testid={`patient-${k}-input`} /></Field>
  );
  const ta = (k) => <Field label={t(`pf_${k}`)}><textarea rows={2} value={f[k] ?? ""} onChange={set(k)} className={`${inputCls} h-auto py-2`} data-testid={`patient-${k}-input`} /></Field>;
  const sel = (k, opts, render) => (
    <Field label={t(`pf_${k}`)}><select value={f[k] ?? ""} onChange={set(k)} className={inputCls} data-testid={`patient-${k}-select`}><option value="">—</option>{opts.map((o) => <option key={o} value={o}>{render ? render(o) : o}</option>)}</select></Field>
  );
  const save = () => {
    if (!f.name.trim()) return toast.error(t("name_required"));
    const data = { ...f, id: patient?.id || uuid(), name: f.name.trim(), file_no: f.file_no.trim() || patient?.file_no || nextFileNo(view.patients) };
    if (dispatch("patient.upsert", data)) { toast.success(t("saved")); onSaved?.(data); }
  };
  const minor = f.birth_date && (Date.now() - new Date(f.birth_date)) / 31557600000 < 18;
  return (
    <div className="space-y-3" data-testid="patient-form">
      <Section title={t("pf_sec_identity")}>
        <Field label={t("name")}><input value={f.name} onChange={set("name")} className={inputCls} data-testid="patient-name-input" /></Field>
        {inp("father_name")}{inp("mother_name")}
        <Field label={t("pf_file_no")}><input dir="ltr" value={f.file_no} onChange={set("file_no")} placeholder={patient?.id ? "" : `${t("auto")}: ${nextFileNo(view.patients)}`} className={inputCls} data-testid="patient-file_no-input" /></Field>
        <Field label={t("hr_birth_date")}><input type="date" dir="ltr" value={f.birth_date} onChange={set("birth_date")} className={inputCls} data-testid="patient-birth-input" /></Field>
        {inp("birth_place")}
        <Field label={t("gender")}>
          <div className="flex gap-2">{["male", "female"].map((g) => <Chip key={g} active={f.gender === g} onClick={() => set("gender")(g)} data-testid={`patient-gender-${g}`}>{t(`gender_${g}`)}</Chip>)}</div>
        </Field>
        <Field label={t("hr_id_number")}><input dir="ltr" value={f.id_number} onChange={set("id_number")} className={inputCls} data-testid="patient-id-input" /></Field>
        {inp("nationality")}
        {sel("marital", MARITAL, (o) => t(`marital_${o}`))}
        {inp("occupation")}{inp("education")}
      </Section>
      <Section title={t("pf_sec_contact")}>
        <Field label={t("hr_phone")}><input dir="ltr" value={f.phone} onChange={set("phone")} className={inputCls} data-testid="patient-phone-input" /></Field>
        {inp("phone2", { ltr: true })}{inp("email", { ltr: true, type: "email" })}
        {sel("wilaya", WILAYAS, (o) => t(`wil_${o}`))}
        {inp("daira")}
        <Field label={t("hr_address")}><input value={f.address} onChange={set("address")} className={inputCls} data-testid="patient-address-input" /></Field>
        <Field label={t("hr_emergency")}><input value={f.emergency_contact} onChange={set("emergency_contact")} className={inputCls} data-testid="patient-emergency-input" /></Field>
        {inp("emergency_phone", { ltr: true })}{inp("emergency_relation")}
        {minor && <>{inp("guardian_name")}{inp("guardian_relation")}{inp("guardian_phone", { ltr: true })}</>}
      </Section>
      <Section title={t("pf_sec_admin")}>
        {inp("insurer")}{inp("policy_no", { ltr: true })}{inp("referred_by")}
      </Section>
      <Section title={t("pf_sec_medical")}>
        <Field label={t("blood_type")}>
          <select value={f.blood_type} onChange={set("blood_type")} className={inputCls} data-testid="patient-blood-select">{BLOOD.map((b) => <option key={b} value={b}>{b || "—"}</option>)}</select>
        </Field>
        {inp("height", { ltr: true, type: "number" })}{inp("weight", { ltr: true, type: "number" })}
        {sel("smoking", ["never", "former", "current"], (o) => t(`smoke_${o}`))}
        {sel("vaccination", ["complete", "partial", "none", "unknown"], (o) => t(`vacc_${o}`))}
        {inp("disability")}
      </Section>
      <Section title={t("pf_sec_history")} cols="sm:grid-cols-2">
        <Field label={t("allergies")}><textarea rows={2} value={f.allergies} onChange={set("allergies")} className={`${inputCls} h-auto py-2`} data-testid="patient-allergies-input" /></Field>
        <Field label={t("chronic")}><textarea rows={2} value={f.chronic} onChange={set("chronic")} className={`${inputCls} h-auto py-2`} data-testid="patient-chronic-input" /></Field>
        <Field label={t("current_meds")}><textarea rows={2} value={f.medications} onChange={set("medications")} className={`${inputCls} h-auto py-2`} data-testid="patient-meds-input" /></Field>
        {ta("surgeries")}{ta("family_history")}
        <Field label={t("hr_notes")}><textarea rows={2} value={f.notes} onChange={set("notes")} className={`${inputCls} h-auto py-2`} data-testid="patient-notes-input" /></Field>
      </Section>
      <BigButton onClick={save} className="w-full" data-testid="patient-save-btn"><Save className="w-5 h-5" />{t("save")}</BigButton>
    </div>
  );
}
